import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { formatPrice } from '../../lib/utils';

// Track images from Unsplash
const TRACK_IMAGES = {
  mainCircuit: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  dragStrip: 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=800&q=80',
  skidPan: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800&q=80',
};

const tracks = [
  {
    id: 'main-circuit',
    name: 'Main Circuit',
    color: 'red',
    price: 450000,
    image: TRACK_IMAGES.mainCircuit,
    description: '2.75km FIA Grade 3 circuit with 12 challenging corners. Perfect for racing teams and serious track day enthusiasts.',
    features: [
      'FIA Grade 3 certified',
      '2.75km total length',
      '12 technical corners',
      '15m track width',
      'Professional pit facilities',
      'Timing system included',
    ],
  },
  {
    id: 'drag-strip',
    name: 'Drag Strip',
    color: 'blue',
    price: 320000,
    image: TRACK_IMAGES.dragStrip,
    description: 'Quarter-mile IHRA-spec drag strip with professional timing tower and spectator facilities.',
    features: [
      'IHRA specification',
      'Quarter-mile distance',
      'Professional timing system',
      'Burnout box',
      'Spectator grandstands',
      'Sound barriers',
    ],
  },
  {
    id: 'skid-pan',
    name: 'Skid Pan',
    color: 'green',
    price: 180000,
    image: TRACK_IMAGES.skidPan,
    description: 'Purpose-built driver training and drift area with wet and dry surface options.',
    features: [
      'Wet surface capability',
      'Drift-ready surface',
      'Variable traction zones',
      'Training equipment available',
      'Instruction available',
      'Corporate events welcome',
    ],
  },
];

const colorClasses = {
  red: { bg: 'bg-red-600', light: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', gradient: 'from-red-600 to-red-800' },
  blue: { bg: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', gradient: 'from-blue-600 to-blue-800' },
  green: { bg: 'bg-green-600', light: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', gradient: 'from-green-600 to-green-800' },
};

export function TrackRentalsPage() {
  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Track Rentals</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Book exclusive access to our world-class facilities. Perfect for racing
            teams, corporate events, and track day enthusiasts.
          </p>
        </div>

        {/* Tracks Grid */}
        <div className="space-y-12 mb-16">
          {tracks.map((track, index) => {
            const colors = colorClasses[track.color as keyof typeof colorClasses];
            const isReversed = index % 2 === 1;

            return (
              <div
                key={track.id}
                className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 items-center`}
              >
                {/* Image */}
                <div className="lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={track.image}
                      alt={track.name}
                      className="w-full h-72 lg:h-96 object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${colors.gradient} opacity-20`} />
                    <div className="absolute top-4 left-4">
                      <span className={`px-4 py-2 ${colors.bg} text-white font-bold rounded-full`}>
                        {track.name}
                      </span>
                    </div>
                    <div className="absolute bottom-4 right-4">
                      <span className="px-4 py-2 bg-white/90 backdrop-blur text-gray-900 font-bold rounded-full">
                        {formatPrice(track.price)}/slot
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="lg:w-1/2">
                  <h2 className={`text-3xl font-bold mb-4 ${colors.text}`}>{track.name}</h2>
                  <p className="text-gray-600 text-lg mb-6">{track.description}</p>

                  {/* Features */}
                  <ul className="grid grid-cols-2 gap-3 mb-6">
                    {track.features.map((feature, i) => (
                      <li key={i} className="flex items-center space-x-2">
                        <div className={`w-5 h-5 rounded-full ${colors.light} flex items-center justify-center`}>
                          <Check size={12} className={colors.text} />
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={`/book?track=${track.id}`}
                    className={`inline-flex items-center px-6 py-3 ${colors.bg} text-white font-medium rounded-xl hover:opacity-90 transition-opacity`}
                  >
                    Book {track.name}
                    <ArrowRight size={18} className="ml-2" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="bg-gray-900 rounded-3xl p-8 md:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold mb-6">What's Included</h2>
              <ul className="space-y-4">
                {[
                  'Exclusive track access for your session',
                  'Professional marshals and safety staff',
                  'Access to pit lane facilities',
                  'Timing equipment and live results',
                  'Basic track preparation and maintenance',
                  'On-site medical support',
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-6">Booking Process</h2>
              <ol className="space-y-4">
                {[
                  { step: '1', text: 'Create an account and register your team' },
                  { step: '2', text: 'Submit a booking request for your preferred slot' },
                  { step: '3', text: 'Our team will review and approve your request' },
                  { step: '4', text: 'Receive confirmation and invoice via email' },
                  { step: '5', text: 'Complete payment to secure your booking' },
                ].map((item) => (
                  <li key={item.step} className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-white text-gray-900 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      {item.step}
                    </div>
                    <span className="text-gray-300">{item.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-700 text-center">
            <p className="text-gray-400 mb-4">Ready to book your track experience?</p>
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors"
            >
              Create Your Team Account
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
